import type { FormEvent } from "react";
import { useEffect, useState } from "react";

import { Dialog } from "@/components/ui/Dialog";
import type { UserOut } from "@/lib/api";

interface Props {
  open: boolean;
  user: UserOut | null;
  onClose: () => void;
  onSubmit: (password: string) => Promise<void>;
}

export function ResetPasswordDialog({ open, user, onClose, onSubmit }: Props) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPassword("");
    setConfirm("");
    setError(null);
  }, [open, user]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Senha deve ter pelo menos 8 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("As senhas não coincidem.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(password);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível redefinir a senha.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      title={user ? `Redefinir senha — ${user.name}` : "Redefinir senha"}
      open={open}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-ink-600 px-4 py-2 text-sm font-medium text-ink-200 hover:bg-ink-800"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="reset-password-form"
            disabled={submitting}
            className="rounded bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500 disabled:opacity-50"
          >
            {submitting ? "Salvando…" : "Redefinir"}
          </button>
        </>
      }
    >
      <form id="reset-password-form" className="space-y-4" onSubmit={(e) => void handleSubmit(e)}>
        <p className="text-xs text-ink-500">
          O usuário precisará usar a nova senha no próximo login.
        </p>
        <div className="space-y-2">
          <label htmlFor="pwd-new" className="block text-xs font-medium text-ink-400">
            Nova senha
          </label>
          <input
            id="pwd-new"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            className="w-full rounded border border-ink-600 bg-ink-900 px-3 py-2 text-sm text-ink-100 outline-none ring-violet-500/40 focus:ring-2"
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="pwd-confirm" className="block text-xs font-medium text-ink-400">
            Confirmar senha
          </label>
          <input
            id="pwd-confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            className="w-full rounded border border-ink-600 bg-ink-900 px-3 py-2 text-sm text-ink-100 outline-none ring-violet-500/40 focus:ring-2"
          />
        </div>
        {error && <p className="text-sm text-red-400">{error}</p>}
      </form>
    </Dialog>
  );
}
